import { Box, Divider, Group, Paper, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import {
  IconBook,
  IconCalculator,
  IconEye,
  IconPuzzle,
  IconStarFilled,
  IconTarget,
  TablerIcon,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { AXIS_PRESENTATION, AxisKey, ProfessionMatch } from "@utils/cognitiveProfile";
import { useCognitiveColor } from "../hooks/useCognitiveFillVariant";

const ICON_BY_NAME: Record<string, TablerIcon> = {
  puzzle: IconPuzzle,
  book: IconBook,
  calculator: IconCalculator,
  target: IconTarget,
  eye: IconEye,
};

interface CognitiveTopProfessionsCardProps {
  matches: ProfessionMatch[];
}

const RANK_FONT_SIZE: Record<number, number> = { 1: 36, 2: 28, 3: 20, 4: 20, 5: 20 };

const rankFontSize = (rank: number): number => RANK_FONT_SIZE[rank] ?? 20;

const titleOrderForRank = (rank: number): 3 | 4 | 5 => {
  if (rank === 1) return 3;
  if (rank === 2) return 4;
  return 5;
};

export function CognitiveTopProfessionsCard({ matches }: CognitiveTopProfessionsCardProps) {
  const { t } = useTranslation();
  const colorFor = useCognitiveColor();

  return (
    <Paper p="lg" radius="md" withBorder shadow="md" h="100%">
      <Stack gap={2} mb="sm">
        <Text size="xs" c="dimmed" tt="uppercase" fw={700} lts={1.2}>
          {t("cognitiveProfile.topProfessions.eyebrow")}
        </Text>
        <Title order={4}>{t("cognitiveProfile.topProfessions.heading")}</Title>
        <Text size="xs" c="dimmed">
          {t("cognitiveProfile.topProfessions.intro")}
        </Text>
      </Stack>

      {matches.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t("cognitiveProfile.topProfessions.empty")}
        </Text>
      ) : (
        <Stack gap={0}>
          {matches.map((match, idx) => {
            const rank = idx + 1;
            return (
              <Box key={match.key}>
                <Group align="center" wrap="nowrap" py="xs" gap="sm">
                  <Text
                    fz={rankFontSize(rank)}
                    fw={300}
                    c="dimmed"
                    lh={1}
                    style={{ minWidth: 40, textAlign: "right" }}
                  >
                    {rank}
                  </Text>

                  <Group gap="xs" align="center" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    <Title order={titleOrderForRank(rank)} lh={1.2} tt="none">
                      {t(`cognitiveProfile.professions.${match.key}`, {
                        defaultValue: match.key,
                      })}
                    </Title>
                    {rank === 1 && (
                      <ThemeIcon
                        size="md"
                        radius="xl"
                        color={colorFor("orange")}
                        variant="filled"
                        title={t("cognitiveProfile.topProfessions.topMatchTitle")}
                      >
                        <IconStarFilled size={14} />
                      </ThemeIcon>
                    )}
                  </Group>

                  <Group gap={4} wrap="nowrap" justify="flex-end" style={{ flexShrink: 0 }}>
                    {match.axes.map((axisKey: AxisKey) => {
                      const presentation = AXIS_PRESENTATION[axisKey];
                      const Icon = ICON_BY_NAME[presentation.iconName];
                      return (
                        <ThemeIcon
                          key={axisKey}
                          size="md"
                          radius="sm"
                          color={colorFor(presentation.color)}
                          variant="filled"
                          title={t(`cognitiveProfile.axes.${axisKey}`)}
                        >
                          <Icon size={14} />
                        </ThemeIcon>
                      );
                    })}
                  </Group>
                </Group>
                {idx < matches.length - 1 && <Divider />}
              </Box>
            );
          })}
        </Stack>
      )}

      <Text size="xs" c="dimmed" mt="sm" fs="italic">
        {t("cognitiveProfile.topProfessions.disclaimer")}
      </Text>
    </Paper>
  );
}
